import React, { useState } from "react";
import { useAnonymizedReport } from "../hooks/useAnonymizedReport";

const AnonymizedReportView = () => {
    const { filtered, loading, municipiosList, filters, setFilters } = useAnonymizedReport();
    const [showExportOptions, setShowExportOptions] = useState(false);

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleExport = (format) => {
        try {
            if (filtered.length === 0) {
                alert("No hay datos para exportar con los filtros actuales.");
                return;
            }

            const headers = [
                "ID_Caso", "ID_Paciente_Anonimizado", "Fecha_Registro", "Edad", "Sexo",
                "Municipio", "Zona", "Latitud_Aproximada", "Longitud_Aproximada",
                "Ascaris_lumbricoides_Presente", "Trichuris_trichiura_Presente", "Uncinarias_Presentes",
                "Conteo_HPG_Ascaris", "Conteo_HPG_Trichuris", "Conteo_HPG_Uncinarias",
                "Intensidad_Infección_Ascaris", "Intensidad_Infección_Trichuris", "Intensidad_Infección_Uncinarias",
                "Número_Parásitos_Detectados", "Nivel_Riesgo_Epidemiológico", "Periodo_Epidemiológico", "Condición_Climática_Asociada",
                "Temperatura_Promedio", "Humedad_Promedio", "Estado_Caso", "Codigo_Proyecto_Investigacion"
            ];

            const rows = filtered.map(c => [
                c.ID_Caso, c.ID_Paciente_Anonimizado, c.Fecha_Registro, c.Edad, c.Sexo,
                c.Municipio, c.Zona, c.Latitud_Aproximada, c.Longitud_Aproximada,
                c.Ascaris_lumbricoides_Presente, c.Trichuris_trichiura_Presente, c.Uncinarias_Presentes,
                c.Conteo_HPG_Ascaris, c.Conteo_HPG_Trichuris, c.Conteo_HPG_Uncinarias,
                c.Intensidad_Infección_Ascaris, c.Intensidad_Infección_Trichuris, c.Intensidad_Infección_Uncinarias,
                c.Número_Parásitos_Detectados, c.Nivel_Riesgo_Epidemiológico, c.Periodo_Epidemiológico,
                c.Condición_Climática_Asociada, c.Temperatura_Promedio, c.Humedad_Promedio, c.Estado_Caso,
                c.Codigo_Proyecto_Investigacion
            ]);

            const fileName = `reporte_anonimizado_${new Date().toISOString().split('T')[0]}`;

            let blob;
            let finalFileName = fileName;

            if (format === 'csv') {
                const escapedRows = rows.map(row =>
                    row.map(val => {
                        const s = String(val);
                        if (s.includes(",") || s.includes("\n") || s.includes('"')) {
                            return `"${s.replace(/"/g, '""')}"`;
                        }
                        return s;
                    }).join(",")
                );
                const csvContent = headers.join(",") + "\n" + escapedRows.join("\n");
                blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8" });
                finalFileName += ".csv";
            } else if (format === 'json') {
                const jsonData = JSON.stringify(filtered.map(c => {
                    const { id, paciente, ...rest } = c;
                    return rest;
                }), null, 2);
                blob = new Blob([jsonData], { type: "application/json" });
                finalFileName += ".json";
            } else if (format === 'xlsx') {
                let html = `<html><head><meta charset="UTF-8"><style>table{border-collapse:collapse;}th{background:#6B8E23;color:white;padding:5px;}td{padding:5px;border:1px solid #ccc;}</style></head><body><table border="1"><thead><tr>`;
                headers.forEach(h => { html += `<th>${h}</th>`; });
                html += `</tr></thead><tbody>`;
                rows.forEach(r => {
                    html += `<tr>`;
                    r.forEach(v => { html += `<td>${v}</td>`; });
                    html += `</tr>`;
                });
                html += `</tbody></table></body></html>`;
                blob = new Blob([html], { type: "application/vnd.ms-excel" });
                finalFileName += ".xls";
            }

            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", finalFileName);
            document.body.appendChild(link);
            link.click();

            setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }, 100);

        } catch (error) {
            console.error("Export error:", error);
            alert("Ocurrió un error al generar el archivo. Por favor reintente.");
        }
    };

    return (
        <div className="container-fluid px-0">
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-3">
                    <div className="row g-3 align-items-end">
                        <div className="col-md-2">
                            <label className="form-label small fw-bold text-muted">Desde</label>
                            <input type="date" className="form-control form-control-sm" value={filters.startDate} onChange={e => handleFilterChange('startDate', e.target.value)} />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-bold text-muted">Hasta</label>
                            <input type="date" className="form-control form-control-sm" value={filters.endDate} onChange={e => handleFilterChange('endDate', e.target.value)} />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-bold text-muted">Municipio</label>
                            <select className="form-select form-select-sm" value={filters.municipio} onChange={e => handleFilterChange('municipio', e.target.value)}>
                                <option value="">Todos</option>
                                {municipiosList.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-bold text-muted">Riesgo</label>
                            <select className="form-select form-select-sm" value={filters.riesgo} onChange={e => handleFilterChange('riesgo', e.target.value)}>
                                <option value="">Todos</option>
                                <option value="ALTO">ALTO</option>
                                <option value="MEDIO">MEDIO</option>
                                <option value="BAJO">BAJO</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-bold text-muted">Proyecto</label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Buscar proyecto..."
                                value={filters.proyecto}
                                onChange={e => handleFilterChange('proyecto', e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-bold text-muted">Parásito</label>
                            <select className="form-select form-select-sm" value={filters.parasito} onChange={e => handleFilterChange('parasito', e.target.value)}>
                                <option value="">Todos</option>
                                <option value="Ascaris">Ascaris</option>
                                <option value="Trichuris">Trichuris</option>
                                <option value="Uncinarias">Uncinarias</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-bold text-muted">Resultado</label>
                            <select className="form-select form-select-sm" value={filters.resultado} onChange={e => handleFilterChange('resultado', e.target.value)} disabled={!filters.parasito}>
                                <option value="POSITIVO">POSITIVO</option>
                                <option value="NEGATIVO">NEGATIVO</option>
                            </select>
                        </div>
                        <div className="col-md-2 text-end">
                            <div className="dropdown d-inline-block position-relative">
                                <button
                                    className="btn btn-sm btn-success dropdown-toggle d-inline-flex align-items-center gap-2"
                                    type="button"
                                    onClick={() => setShowExportOptions(!showExportOptions)}
                                    onBlur={() => setTimeout(() => setShowExportOptions(false), 200)}
                                >
                                    <i className="bi bi-download"></i> Exportar Datos
                                </button>
                                <ul className={`dropdown-menu dropdown-menu-end shadow border-0 ${showExportOptions ? 'show' : ''}`}
                                    style={{
                                        display: showExportOptions ? 'block' : 'none',
                                        position: 'absolute',
                                        right: 0,
                                        top: '100%',
                                        zIndex: 1050,
                                        minWidth: '200px'
                                    }}>
                                    <li><button className="dropdown-item py-2" onClick={() => handleExport('csv')}><i className="bi bi-filetype-csv me-2 text-success"></i> Formato CSV</button></li>
                                    <li><button className="dropdown-item py-2" onClick={() => handleExport('json')}><i className="bi bi-filetype-json me-2 text-warning"></i> Formato JSON</button></li>
                                    <li><button className="dropdown-item py-2" onClick={() => handleExport('xlsx')}><i className="bi bi-file-earmark-spreadsheet me-2 text-primary"></i> Formato Excel (.xls)</button></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="table-responsive" style={{ maxHeight: '600px' }}>
                    <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                        <thead className="bg-light sticky-top">
                            <tr>
                                <th>ID Caso</th>
                                <th>ID Paciente (Anon)</th>
                                <th>Fecha</th>
                                <th>Edad / Sexo</th>
                                <th>Municipio / Zona</th>
                                <th>Ubicación (Aprox)</th>
                                <th>Parásitos</th>
                                <th>Riesgo</th>
                                <th>Periodo</th>
                                <th>Clima</th>
                                <th>Proyecto</th>
                                <th>Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="12" className="text-center py-4">Cargando datos extendidos...</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan="12" className="text-center py-4 text-muted">No hay datos para exportar.</td></tr>
                            ) : (
                                filtered.map(c => (
                                    <tr key={c.id}>
                                        <td className="fw-bold">{c.ID_Caso}</td>
                                        <td className="text-muted font-monospace">{c.ID_Paciente_Anonimizado}</td>
                                        <td>{c.Fecha_Registro}</td>
                                        <td>{c.Edad} / {c.Sexo}</td>
                                        <td>{c.Municipio} ({c.Zona})</td>
                                        <td>{c.Latitud_Aproximada}, {c.Longitud_Aproximada}</td>
                                        <td>
                                            <div className="d-flex flex-column gap-1">
                                                {c.Ascaris_lumbricoides_Presente === "Sí" && <span className="badge bg-info p-1">Asc: {c.Intensidad_Infección_Ascaris}</span>}
                                                {c.Trichuris_trichiura_Presente === "Sí" && <span className="badge bg-warning text-dark p-1">Tri: {c.Intensidad_Infección_Trichuris}</span>}
                                                {c.Uncinarias_Presentes === "Sí" && <span className="badge bg-secondary p-1">Unc: {c.Intensidad_Infección_Uncinarias}</span>}
                                                {c.Número_Parásitos_Detectados === 0 && <span className="text-muted">Ninguno</span>}
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`badge ${c.Nivel_Riesgo_Epidemiológico === 'ALTO' ? 'bg-danger' : c.Nivel_Riesgo_Epidemiológico === 'MEDIO' ? 'bg-warning text-dark' : 'bg-success'}`}>
                                                {c.Nivel_Riesgo_Epidemiológico}
                                            </span>
                                        </td>
                                        <td>{c.Periodo_Epidemiológico}</td>
                                        <td>{c.Condición_Climática_Asociada}</td>
                                        <td><span className="badge bg-light text-success border-success" style={{ fontSize: '0.65rem' }}>{c.Codigo_Proyecto_Investigacion}</span></td>
                                        <td><span className="badge bg-light text-dark border">{c.Estado_Caso}</span></td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AnonymizedReportView;
